import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { rmcTermsService } from "../../service/rmcTerms.service";

interface PropertyTermsProps {
  propertyId: number;
  proposalPropertyMappingId?: number;
}

const PropertyTerms: React.FC<PropertyTermsProps> = ({
  propertyId,
  proposalPropertyMappingId,
}) => {
  const [customTerms, setCustomTerms] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const userRole = useSelector((state: any) => state.auth.userRole) ?? "";

  // Default standard terms (same as in ProposalPreview)
  const defaultTerms = [
    "This proposal is valid for the period mentioned above and subject to property availability.",
    "Rates are subject to change without prior notice if booked after the valid period.",
    "All payments must be made as per the agreed credit terms.",
    "Cancellation policy applies as per standard corporate housing agreement.",
    "Any damage to the property will be charged separately and is not included in the quoted rates.",
    "Check-in and check-out times are as per property standard policies.",
    "Additional services requested beyond the standard package will be charged separately.",
    "The rates quoted are exclusive of any applicable taxes unless otherwise stated.",
  ];

  // Fetch custom RMC terms for this property if proposalPropertyMappingId is provided
  useEffect(() => {
    const fetchCustomTerms = async () => {
      if (!proposalPropertyMappingId) {
        return;
      }

      setLoading(true);
      try {
        const termsArray = await rmcTermsService.getByProposalPropertyMappingId(
          proposalPropertyMappingId
        );
        // getByProposalPropertyMappingId returns an array, take the first (and should be only) item
        if (
          termsArray &&
          termsArray.length > 0 &&
          termsArray[0].termsAndConditions
        ) {
          setCustomTerms(termsArray[0].termsAndConditions);
        }
      } catch (error) {
        console.error("Error fetching custom terms:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomTerms();
  }, [proposalPropertyMappingId]);

  // Only show this component for B2B users
  if (userRole !== "B2BUser") {
    return null;
  }

  // Don't show anything if there are no custom terms
  if (!customTerms && !proposalPropertyMappingId) {
    return null;
  }

  return (
    <div className="property-terms">
      <style>
        {`
          .property-terms {
            margin-top: 1.5rem;
          }

          .property-terms-header {
            font-size: 1.1rem;
            font-weight: 600;
            color: #2c5aa0;
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #2c5aa0;
          }

          .property-terms-section {
            background: #f8f9fa;
            border-left: 4px solid #2c5aa0;
            padding: 1rem 1.25rem;
            border-radius: 4px;
            margin-bottom: 1rem;
          }

          .property-terms-section h6 {
            font-size: 0.95rem;
            font-weight: 600;
            color: #333;
            margin-bottom: 0.75rem;
          }

          .property-terms-list {
            list-style: none;
            padding: 0;
            margin: 0;
          }

          .property-terms-list li {
            padding: 0.5rem 0;
            padding-left: 1.5rem;
            position: relative;
            color: #555;
            font-size: 0.9rem;
            line-height: 1.6;
          }

          .property-terms-list li:before {
            content: "✓";
            position: absolute;
            left: 0;
            color: #2e7d32;
            font-weight: bold;
          }

          .property-terms-custom {
            background: #fff3cd;
            border-left-color: #ff8a00;
            margin-top: 1rem;
          }

          .property-terms-custom-text {
            white-space: pre-wrap;
            color: #333;
            font-size: 0.9rem;
            line-height: 1.8;
          }

          .property-terms-loading {
            text-align: center;
            padding: 1rem;
            color: #666;
          }
        `}
      </style>

      <div className="property-terms-header">
        <i className="pi pi-file-edit me-2"></i>
        Terms & Conditions for This Property
      </div>

      {/* Standard Terms */}
      <div className="property-terms-section">
        <h6>Standard Terms</h6>
        <ul className="property-terms-list">
          {defaultTerms.map((term, index) => (
            <li key={index}>{term}</li>
          ))}
        </ul>
      </div>

      {/* Custom RMC Terms */}
      {loading ? (
        <div className="property-terms-loading">
          <i className="pi pi-spinner pi-spin"></i> Loading custom terms...
        </div>
      ) : (
        customTerms && (
          <div className="property-terms-section property-terms-custom">
            <h6>
              <i
                className="pi pi-star-fill me-2"
                style={{ color: "#ff8a00" }}
              ></i>
              Property-Specific Terms
            </h6>
            <div className="property-terms-custom-text">{customTerms}</div>
          </div>
        )
      )}
    </div>
  );
};

export default PropertyTerms;
